/**
 * @copyright Copyright 2023-2025 Epic Systems Corporation
 * @file Disclaimer that shows above a questionnaire
 * @module Epic.AppOrchard.Showroom.QuestionnaireDisclaimer
 */

import { Text, TextProps } from "@chakra-ui/react";
import { AOTooltip } from "ao/components/Core";
import { QuestionnaireType } from "ao/data";
import { FunctionComponent, memo } from "react";
import { BsFillQuestionCircleFill } from "react-icons/bs";

interface IProps {
	topLevelTierSingleName: string;
	hideEndorsementWarning: boolean;
	type: QuestionnaireType;
}

type Props = IProps & TextProps;

export const QuestionnaireDisclaimer: FunctionComponent<Props> = memo((props) => {
	const { topLevelTierSingleName, hideEndorsementWarning, type, ...rest } = props;

	return (
		<Text color={rest.color || "green.600"} {...rest}>
			{type === QuestionnaireType.AppReadiness
				? "This optional, vendor-completed questionnaire inquires about general safety, privacy, and security practices."
				: `This optional, vendor-completed questionnaire inquires about how this ${topLevelTierSingleName.toLocaleLowerCase()} handles patient data.`}
			{!hideEndorsementWarning && (
				<AOTooltip
					pl="0.25em"
					helptext="The information in this questionnaire is provided by the vendor. Epic does not verify vendor responses."
				>
					<BsFillQuestionCircleFill />
				</AOTooltip>
			)}
		</Text>
	);
});
